<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { useGlobalSearch } from '@/composables/useGlobalSearch'

/**
 * Modal de búsqueda global (Cmd/Ctrl+K).
 *
 * Busca en el contenido de todos los EPUBs y en las anotaciones, agrupando
 * resultados por libro. Al pulsar un resultado salta al lector (con CFI si
 * es una anotación, o a la última posición si es texto del libro).
 */

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const { t } = useI18n()
const router = useRouter()
const root = ref<HTMLElement | null>(null)

const { query, groups, hits, searching, ready, init, clear } = useGlobalSearch()

// El índice de búsqueda se construye la PRIMERA vez que se abre el modal, no
// al arrancar la app: con muchos libros la reconstrucción del índice bloquea
// el primer render de la biblioteca. Mientras carga, el input está deshabilitado
// con el texto "indexando" que ya existía.
watch(
  () => props.open,
  (open) => {
    if (open) void init()
  },
  { immediate: true },
)

useFocusTrap(root, () => props.open, {
  onEscape: () => close(),
  autofocus: true,
})

function close() {
  emit('update:open', false)
}

function openHit(bookId: string, cfi: string | null) {
  close()
  const cfiQuery = cfi ? { cfi } : undefined
  void router.push({ name: 'reader', params: { bookId }, query: cfiQuery })
}

function reset() {
  clear()
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-[70] flex items-start justify-center bg-black/50 p-4 pt-[10vh]"
        @click.self="close"
      >
        <div
          ref="root"
          role="dialog"
          aria-modal="true"
          :aria-label="t('search.title')"
          class="flex max-h-[75vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-surface-muted bg-surface shadow-2xl"
        >
          <!-- Input -->
          <div class="flex items-center gap-2 border-b border-surface-muted/60 p-3">
            <svg
              class="h-5 w-5 shrink-0 text-ink-muted"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="7" />
              <path stroke-linecap="round" d="m20 20-3.5-3.5" />
            </svg>
            <input
              ref="first"
              v-model="query"
              type="search"
              :placeholder="ready ? t('search.placeholder') : t('search.indexingBooks')"
              class="flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-muted"
              :disabled="!ready"
              @keydown.esc="close"
            />
            <button
              type="button"
              class="rounded-md border border-surface-muted px-2 py-0.5 text-xs text-ink-muted"
              @click="close"
            >
              Esc
            </button>
          </div>

          <!-- Resultados -->
          <div class="flex-1 overflow-y-auto p-2">
            <div v-if="!ready" class="py-8 text-center text-sm text-ink-muted">
              {{ t('search.indexingBooks') }}
            </div>

            <div
              v-else-if="query.trim().length >= 2 && !searching && hits.length === 0"
              class="py-8 text-center text-sm text-ink-muted"
            >
              {{ t('search.noResults') }}
            </div>

            <div
              v-else-if="query.trim().length >= 2 && searching"
              class="py-8 text-center text-sm text-ink-muted"
            >
              {{ t('search.searching') }}
            </div>

            <div v-else-if="hits.length > 0" class="space-y-1 text-sm">
              <p class="px-2 py-1 text-xs text-ink-muted">
                {{ t('search.resultsCount', { count: hits.length }, hits.length) }}
              </p>
              <template v-for="group in groups" :key="group.bookId">
                <div class="px-2 py-1">
                  <p class="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {{ group.title }}
                    <span v-if="group.author" class="font-normal normal-case"> · {{ group.author }}</span>
                  </p>
                </div>
                <button
                  v-for="hit in group.hits"
                  :key="hit.chunkId"
                  type="button"
                  class="block w-full rounded-lg px-3 py-2 text-left text-sm text-ink-subtle transition hover:bg-surface-muted/40"
                  @click="openHit(hit.bookId, hit.cfi)"
                >
                  <span class="mb-0.5 flex items-center gap-1 text-[11px] text-ink-muted">
                    <span v-if="hit.kind === 'highlight'">🖊️ {{ t('search.annotation') }}</span>
                    <span v-else>📖 {{ t('search.chapter') }}</span>
                  </span>
                  <!-- eslint-disable vue/no-v-html -- makeSnippet escapa todo el HTML salvo <mark> -->
                  <span
                    v-if="hit.snippet"
                    class="line-clamp-2"
                    v-html="hit.snippet"
                  />
                  <!-- eslint-enable vue/no-v-html -->
                  <span v-else class="italic text-ink-muted">{{ group.title }}</span>
                </button>
              </template>
            </div>

            <!-- Estado inicial (sin query) -->
            <div
              v-else
              class="py-8 text-center text-xs text-ink-muted"
            >
              {{ t('search.shortcutHint') }}
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between border-t border-surface-muted/60 px-3 py-2 text-[11px] text-ink-muted">
            <button v-if="query" type="button" class="hover:underline" @click="reset">
              {{ t('common.clear') }}
            </button>
            <span v-else></span>
            <span>{{ t('search.shortcutHint') }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
